import {
  INSPECTION_AUTHORITY_LABELS,
  INSPECTION_RESULT_LABELS,
  INSPECTION_STATUS_LABELS,
  INSPECTION_STATUSES,
  type Inspection,
} from "@station/contracts";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Label,
  MoneyText,
  Select,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClipboardList, Plus } from "lucide-react";
import { type ReactElement, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { InspectionDialog } from "../../../../features/compliance/inspection-dialogs.js";
import { useInspections } from "../../../../features/compliance/queries.js";
import { useMaintenancePermissions } from "../../../../features/maintenance/queries.js";
import { MaintenancePage, StationFilterField } from "../../../../features/maintenance/shared.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatCount, formatDate } from "../../../../lib/format.js";

const searchSchema = z.object({
  station: z.string().uuid().optional().catch(undefined),
  status: z.enum(INSPECTION_STATUSES).optional().catch(undefined),
  open: z.enum(["1"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/uyum/denetimler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: InspectionsRoute,
});

/**
 * Denetim ziyaretleri (Prompt 23 web, `GET /compliance/inspections`): kurum, tarih, durum, sonuç / ceza, düzeltici faaliyet
 * sayıları (açık / geciken). Süzgeçler URL'de; yeni ziyaret planlı ya da yapılmış olarak kaydedilir.
 */
function InspectionsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useMaintenancePermissions();
  const search = Route.useSearch();
  const [creating, setCreating] = useState(false);
  return (
    <MaintenancePage
      title={t("menu.complianceInspections")}
      description={t("compliance.inspections.description")}
      requirement="compliance"
      actions={
        permissions.canManageCompliance ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("compliance.inspections.new")}
          </Button>
        ) : null
      }
    >
      <InspectionList />
      {creating ? (
        <InspectionDialog open onOpenChange={setCreating} defaultStationId={search.station ?? null} />
      ) : null}
    </MaintenancePage>
  );
}

function InspectionList(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const openId = useId();
  const filters: { stationId: string | null; status: string | null; openActions: boolean } = {
    stationId: search.station ?? null,
    status: search.status ?? null,
    openActions: search.open === "1",
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useInspections(filters, pages.cursor);
  const setFilters = (next: typeof filters) =>
    void navigate({
      to: ".",
      search: {
        ...(next.stationId ? { station: next.stationId } : {}),
        ...(next.status ? { status: next.status } : {}),
        ...(next.openActions ? { open: "1" } : {}),
      } as never,
      replace: true,
    });
  const columns = useMemo<DataTableColumn<Inspection>[]>(
    () => [
      {
        id: "authority",
        header: t("compliance.fields.authority"),
        hideable: false,
        cell: (row) => (
          <Link
            to="/uyum/denetimler/$id"
            params={{ id: row.id }}
            className="font-medium underline underline-offset-4"
          >
            {row.authorityName ?? INSPECTION_AUTHORITY_LABELS[row.authority]}
          </Link>
        ),
      },
      { id: "station", header: t("maintenance.fields.station"), cell: (row) => row.stationCode },
      {
        id: "visitDate",
        header: t("compliance.fields.visitDate"),
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDate(row.visitDate)}</span>,
      },
      {
        id: "status",
        header: t("compliance.fields.inspectionStatus"),
        cell: (row) => (
          <span className="grid justify-items-start gap-1">
            <StatusBadge
              tone={row.status === "PLANNED" ? "info" : row.status === "COMPLETED" ? "success" : "neutral"}
            >
              {INSPECTION_STATUS_LABELS[row.status]}
            </StatusBadge>
            {row.result ? (
              <span className="text-xs text-muted-foreground">
                {INSPECTION_RESULT_LABELS[row.result]}
                {row.penaltyAmount ? " · " : ""}
                {row.penaltyAmount ? <MoneyText value={row.penaltyAmount} /> : null}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: "actions",
        header: t("compliance.fields.correctiveActions"),
        cell: (row) =>
          row.actions.total === 0 ? (
            "—"
          ) : (
            <span className="grid justify-items-start gap-0.5 text-sm">
              <span>
                {t("compliance.inspections.actionCounts", {
                  open: formatCount(row.actions.open),
                  total: formatCount(row.actions.total),
                })}
              </span>
              {row.actions.overdue > 0 ? (
                <StatusBadge tone="danger">
                  {t("compliance.summary.overdue", { count: row.actions.overdue })}
                </StatusBadge>
              ) : null}
            </span>
          ),
      },
    ],
    [t],
  );
  return (
    <DataTable
      caption={t("menu.complianceInspections")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      filters={
        <>
          <StationFilterField
            value={filters.stationId}
            onChange={(stationId) => setFilters({ ...filters, stationId })}
          />
          <FormField label={t("compliance.fields.inspectionStatus")} className="w-40">
            {(control) => (
              <Select
                {...control}
                value={filters.status ?? ""}
                onChange={(event) => setFilters({ ...filters, status: event.target.value || null })}
              >
                <option value="">{t("common.all")}</option>
                {INSPECTION_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {INSPECTION_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <div className="flex h-10 items-center gap-2">
            <Checkbox
              id={openId}
              checked={filters.openActions}
              onChange={(event) => setFilters({ ...filters, openActions: event.target.checked })}
            />
            <Label htmlFor={openId}>{t("compliance.inspections.openActionsOnly")}</Label>
          </div>
        </>
      }
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<ClipboardList />}
          title={t("compliance.inspections.emptyTitle")}
          description={t("compliance.inspections.emptyDescription")}
        />
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
